import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuoteRequest = () => {
  const location = useLocation();
  const { language } = useLanguage();
  const t = translations[language];
  const [loading, setLoading] = useState(false);
  const [surgeries, setSurgeries] = useState([]);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    country: '',
    language: language,
    surgery_id: '',
    surgery_name: '',
    message: '',
  });

  useEffect(() => {
    fetchSurgeries();
    
    // If surgery passed from navigation
    if (location.state?.surgery) {
      const surgery = location.state.surgery;
      setFormData((prev) => ({
        ...prev,
        surgery_id: surgery.id,
        surgery_name: surgery[`name_${language}`],
      }));
    }
  }, [location.state, language]);

  const fetchSurgeries = async () => {
    try {
      const response = await axios.get(`${API}/surgeries`);
      setSurgeries(response.data);
    } catch (error) {
      console.error('Error fetching surgeries:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Update surgery name when surgery is selected
    if (name === 'surgery_id' && value) {
      const selectedSurgery = surgeries.find((s) => s.id === value);
      if (selectedSurgery) {
        setFormData((prev) => ({
          ...prev,
          surgery_name: selectedSurgery[`name_${language}`],
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/quote-request`, formData);
      toast.success(t.quoteSuccess, {
        description: t.quoteSuccessDesc,
      });
      
      // Reset form
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        country: '',
        language: language,
        surgery_id: '',
        surgery_name: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting quote request:', error);
      toast.error(t.quoteError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="py-12 bg-gradient-to-br from-gray-50 to-white min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t.quoteFormTitle}
          </h1>
          <p className="text-lg text-gray-600">{t.quoteFormSubtitle}</p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Full Name */}
            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-2">
                {t.fullName} *
              </label>
              <Input
                id="full_name"
                name="full_name"
                type="text"
                required
                value={formData.full_name}
                onChange={handleChange}
                placeholder={t.fullNamePlaceholder}
                data-testid="full-name-input"
              />
            </div>

            {/* Email & Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {t.email} *
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t.emailPlaceholder}
                  data-testid="email-input"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  {t.phone} *
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t.phonePlaceholder}
                  data-testid="phone-input"
                />
              </div>
            </div>

            {/* Country */}
            <div>
              <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                {t.country} *
              </label>
              <Input
                id="country"
                name="country"
                type="text"
                required
                value={formData.country}
                onChange={handleChange}
                placeholder={t.countryPlaceholder}
                data-testid="country-input"
              />
            </div>

            {/* Preferred Language */}
            <div>
              <label htmlFor="language" className="block text-sm font-medium text-gray-700 mb-2">
                {t.preferredLanguage} *
              </label>
              <Select
                value={formData.language}
                onValueChange={(value) => handleSelectChange('language', value)}
              >
                <SelectTrigger data-testid="language-select">
                  <SelectValue placeholder={t.selectLanguage} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fr">{t.french}</SelectItem>
                  <SelectItem value="en">{t.english}</SelectItem>
                  <SelectItem value="ar">{t.arabic}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Surgery Selection */}
            <div>
              <label htmlFor="surgery" className="block text-sm font-medium text-gray-700 mb-2">
                {t.surgeryInterested}
              </label>
              <Select
                value={formData.surgery_id}
                onValueChange={(value) => handleSelectChange('surgery_id', value)}
              >
                <SelectTrigger data-testid="surgery-select">
                  <SelectValue placeholder={t.selectSurgery} />
                </SelectTrigger>
                <SelectContent>
                  {surgeries.map((surgery) => (
                    <SelectItem key={surgery.id} value={surgery.id}>
                      {surgery[`name_${language}`]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                {t.message} *
              </label>
              <Textarea
                id="message"
                name="message"
                required
                rows={6}
                value={formData.message}
                onChange={handleChange}
                placeholder={t.messagePlaceholder}
                data-testid="message-textarea"
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              size="lg"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 rounded-full text-lg"
              data-testid="submit-quote-btn"
            >
              {loading ? t.submitting : t.submit}
            </Button>
          </form>
        </div>
      </div>
    </main>
  );
};

export default QuoteRequest;